import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiChevronDown, FiChevronUp, FiPhone } = FiIcons;

const FAQ = () => {
  const [openFAQ, setOpenFAQ] = useState(null);

  const faqs = [
    {
      question: "What is an AI voice receptionist and how does it work in NYC?",
      answer: "An AI voice receptionist is an intelligent virtual assistant powered by advanced AI technology that answers calls, routes them appropriately, and provides professional customer service 24/7. In NYC, our AI receptionist integrates seamlessly with existing PBX systems and VoIP infrastructure, making it perfect for businesses from Manhattan to Brooklyn. It uses natural language processing to understand caller intent and can handle complex conversations, schedule appointments, and transfer calls to the right department or person."
    },
    {
      question: "Can AI handle calls for my Hudson Valley business?",
      answer: "Absolutely! Our AI voice receptionist is specifically designed to serve businesses throughout the Hudson Valley region, including Poughkeepsie, Kingston, Newburgh, and surrounding areas. It can handle multiple languages, understand regional accents, and integrate with local business systems. Whether you're a medical practice, law firm, or real estate agency in the Hudson Valley, our AI can manage your calls professionally while you focus on serving your community."
    },
    {
      question: "Is an AI-powered voice assistant better than a traditional PBX?",
      answer: "An AI voice assistant doesn't replace your PBX system – it enhances it! While traditional PBX systems handle call routing and basic functions, our AI adds intelligent conversation capabilities, 24/7 availability, and advanced features like natural language understanding, appointment scheduling, and CRM integration. It works alongside your existing PBX/VoIP setup, providing a more sophisticated and cost-effective solution than hiring human receptionists."
    },
    {
      question: "How quickly can I set up an AI receptionist for my small business?",
      answer: "Setup is typically completed within 24-48 hours for most small businesses. Our team handles the technical integration with your existing phone systems, including PBX, VoIP, and 3CX configurations. We provide custom training for your specific business needs, upload your company information, and test all call flows before going live. Most NYC and Hudson Valley businesses are up and running within 2 business days."
    },
    {
      question: "What types of businesses benefit most from AI voice receptionists?",
      answer: "AI voice receptionists are particularly beneficial for service-based businesses including medical practices, law firms, real estate agencies, accounting firms, dental offices, veterinary clinics, and professional services. Any business that receives frequent calls, needs 24/7 availability, or wants to improve customer service while reducing costs will benefit significantly from our AI receptionist solution."
    },
    {
      question: "Does the AI voice assistant support multiple languages for diverse NYC clientele?",
      answer: "Yes! Our AI voice receptionist supports over 50 languages and dialects, making it perfect for NYC's diverse business environment. It can seamlessly switch between languages during calls and understands various accents and speech patterns. This is especially valuable for businesses serving multicultural communities in NYC and the Hudson Valley region."
    },
    {
      question: "How does the AI integrate with my existing CRM and business systems?",
      answer: "Our AI voice receptionist integrates with popular CRM systems including Salesforce, HubSpot, Pipedrive, and many others. It can automatically log calls, update customer records, schedule appointments, and trigger follow-up actions. The integration is seamless and doesn't require changes to your existing workflow – it simply enhances your current systems with intelligent call handling."
    },
    {
      question: "What happens if the AI can't handle a complex call?",
      answer: "Our AI is designed to recognize when a call requires human intervention and can seamlessly transfer the caller to the appropriate person or department. It provides context about the call to the human recipient, ensuring a smooth handoff. The AI continuously learns from these interactions to handle similar situations better in the future, constantly improving its capabilities."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  // Load Sayify script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = "https://resource.sayify.pro/cdn/widget.js";
    script.async = true;
    script.dataset.slug = "customer-inquiry";
    script.dataset.mode = "inline-form";
    script.dataset.baseUrl = "https://crcnu.sayify.pro";
    script.dataset.label = "Feedback";
    script.dataset.color = "#C86A4A";
    script.dataset.position = "bottom-right";
    script.dataset.description = "Share your feedback by voice";
    script.dataset.inlineHeight = "600";
    script.dataset.autoHeight = "true";
    script.dataset.container = "sayify-widget-container";
    
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  return (
    <section className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
          >
            Frequently Asked Questions
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-xl text-gray-600"
          >
            Everything you need to know about AI voice receptionists for your business
          </motion.p>
        </div>

        {/* Try Our Sales Bot Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-10 bg-blue-50 rounded-xl p-5 border border-blue-100 flex flex-col sm:flex-row justify-between items-center"
        >
          <div className="mb-4 sm:mb-0">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Experience our AI technology firsthand</h3>
            <p className="text-gray-600">Call our demo line to interact with our latest AI voice assistant</p>
          </div>
          <a
            href="tel:+13322324531"
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-3 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiPhone} className="w-5 h-5" />
            <span className="font-medium">+1 332-232-4531</span>
          </a>
        </motion.div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-gray-50 rounded-xl overflow-hidden"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-6 text-left flex items-center justify-between hover:bg-gray-100 transition-colors duration-200"
              >
                <h3 className="text-lg font-semibold text-gray-900 pr-4">
                  {faq.question}
                </h3>
                <SafeIcon icon={openFAQ === index ? FiChevronUp : FiChevronDown} className="w-5 h-5 text-gray-500 flex-shrink-0" />
              </button>
              <AnimatePresence>
                {openFAQ === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6">
                      <p className="text-gray-700 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* Sayify Embed */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16 w-full"
          data-section="get-started"
        >
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-8 text-white">
            <h3 className="text-2xl md:text-3xl font-bold mb-4 text-center">
              Ready to Get Started?
            </h3>
            <p className="text-blue-100 mb-6 text-center">
              Share your feedback or inquiry directly by voice using our secure widget below.
            </p>
            <p className="text-center text-blue-100 mb-8">
              Or call our demo line at <a href="tel:+13322324531" className="font-bold text-white hover:text-blue-200 transition-colors">+1 332-232-4531</a> to experience our AI technology firsthand.
            </p>
            
            <div id="sayify-widget-container" className="bg-white rounded-xl shadow-inner overflow-hidden min-h-[600px]">
              {/* Sayify Widget will inject here */}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;